import { useDataPart } from '@ai-sdk-tools/store';
import { useMutation, useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { useDownloadFile } from '@newfront-insurance/admin-ui/files';
import type {
  CoverageGapAnalysisData,
  FolderArtifactData,
  PolicyCheckData,
  SkillOfferData,
} from '@newfront-insurance/ai-api';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { useNotify } from '@newfront-insurance/next-notifications';
import type { UIDataTypes, UIMessagePart, UITools } from 'ai';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { getFileExtension, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from './analytics';
import { useFolderArtifactApply } from './folder-artifact-apply';
import { normalizeSkillCommand, stripLeadingSlash } from '../../../lib/skill-command-utils';
import { GET_CUSTOM_SKILLS, type CustomSkillRecord } from '../../../queries/chats';
import { CREATE_CUSTOM_SKILL, UPDATE_CUSTOM_SKILL, UPDATE_CUSTOM_SKILL_DRAFT } from '../../../queries/mutations';
import { CoverageGapAnalysisStatusCard } from '../../customized-data/coverage-gap-analysis-status-card';
import { PolicyCheckingStatusCard } from '../../customized-data/policy-checking-status-card';
import { ProgressMessage } from '../../ui/progress-message';
import { FolderArtifactCard } from '../components/folder-artifact-card';
import { SkillOfferCard, type SkillOfferAction } from '../components/skill-offer-card';
import { createDefaultToolRenderers as createDefaultToolRenderersFromComponents } from '../components/tools/renderers';
import { useChatOverlayContextSafe } from '../contexts/chat-overlay-context';
import { useSkillsSafe } from '../contexts/skills-context';
import { useCustomSkillShareStatus } from '../hooks/use-custom-skill-share-status';
import { useShowProgressDataPart } from '../hooks/use-show-progress-data-part';

// Type for tool output renderer function
export type ToolOutputRenderer = (part: UIMessagePart<UIDataTypes, UITools>, messageUuid: string) => ReactNode | null;

export interface DataPartRendererProps {
  part?: UIMessagePart<UIDataTypes, UITools>;
}

// Type for data part renderer - now a React component instead of a function
export type DataPartRenderer = (props?: DataPartRendererProps) => ReactNode | null;

function getSkillOfferFallbackCommandHandle(filename: string): string | undefined {
  return filename.replace(/\.[^.]+$/, '').trim() || undefined;
}

export function getDataPartData<T>(part: UIMessagePart<UIDataTypes, UITools> | undefined, type: string): T | null {
  const expectedType = type.startsWith('data-') ? type : `data-${type}`;

  return part?.type === expectedType && 'data' in part ? ((part as { data?: T }).data ?? null) : null;
}

export function getSkillOfferDataForRenderer(
  part: UIMessagePart<UIDataTypes, UITools> | undefined,
  fallbackData: SkillOfferData | null,
): SkillOfferData | null {
  return getDataPartData<SkillOfferData>(part, 'skillOffer') ?? fallbackData;
}

export function getFolderArtifactDataForRenderer(
  part: UIMessagePart<UIDataTypes, UITools> | undefined,
  fallbackData: FolderArtifactData | null,
): FolderArtifactData | null {
  return getDataPartData<FolderArtifactData>(part, 'folderArtifact') ?? fallbackData;
}

export function getSkillOfferCommandHandle(
  data: Pick<SkillOfferData, 'filename' | 'command' | 'commandHandle'>,
): string | undefined {
  return (
    stripLeadingSlash(data.commandHandle ?? data.command).trim() || getSkillOfferFallbackCommandHandle(data.filename)
  );
}

export function findMatchingCustomSkillForOffer<TSkill extends Pick<CustomSkillRecord, 'command'>>(
  commandHandle: string | undefined,
  customSkills: readonly TSkill[] | undefined,
): TSkill | undefined {
  const normalizedOfferCommand = normalizeSkillCommand(commandHandle);

  return normalizedOfferCommand
    ? customSkills?.find((skill) => normalizeSkillCommand(skill.command) === normalizedOfferCommand)
    : undefined;
}

export function isSkillOfferFileApplied(
  fileUuid: string | undefined,
  customSkills: readonly (Pick<CustomSkillRecord, 'sourceFileUuid'> & { sourceFileUuids?: unknown })[] | undefined,
): boolean {
  return Boolean(
    fileUuid &&
      customSkills?.some(
        (skill) =>
          skill.sourceFileUuid === fileUuid ||
          (Array.isArray(skill.sourceFileUuids) && skill.sourceFileUuids.includes(fileUuid)),
      ),
  );
}

export function getSkillOfferAction(
  matchingCustomSkill: Pick<CustomSkillRecord, 'uuid'> | undefined,
  hasShares: boolean,
): SkillOfferAction {
  if (!matchingCustomSkill) {
    return 'save';
  }

  return hasShares ? 'draft' : 'update';
}

/**
 * Coverage Gap Analysis data part component.
 * Uses useDataPart hook to get the latest data.
 */
function CoverageGapAnalysisDataPart() {
  const [data] = useDataPart<CoverageGapAnalysisData>('coverageGapAnalysis');

  if (!data) return null;

  return <CoverageGapAnalysisStatusCard data={data} />;
}

/**
 * Policy Check data part component.
 * Uses useDataPart hook to get the latest data.
 */
function PolicyCheckDataPart() {
  const [data] = useDataPart<PolicyCheckData>('policyCheck');

  if (!data) return null;

  return <PolicyCheckingStatusCard data={data} />;
}

function SkillOfferDataPart({ part }: DataPartRendererProps = {}) {
  const isNovaSkillsManagementEnabled = useFeatureFlag('nova-skills-management', false);
  const notify = useNotify();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const [latestData] = useDataPart<SkillOfferData>('skillOffer');
  const offerData = getSkillOfferDataForRenderer(part, latestData);
  const { download, isDownloading } = useDownloadFile({});
  const skillsContext = useSkillsSafe();
  const [isSaving, setIsSaving] = useState(false);
  const [createCustomSkill] = useMutation(CREATE_CUSTOM_SKILL);
  const [updateCustomSkill] = useMutation(UPDATE_CUSTOM_SKILL);
  const [updateCustomSkillDraft] = useMutation(UPDATE_CUSTOM_SKILL_DRAFT);
  const { data: customSkillsData, refetch: refetchCustomSkills } = useQuery(GET_CUSTOM_SKILLS, {
    variables: {
      filters: {
        enabled: [true, false],
        includeDrafts: true,
      },
    },
    skip: !isNovaSkillsManagementEnabled || !offerData?.fileUuid,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const skillOfferData = offerData?.kind === 'skill' ? offerData : null;
  const commandHandle = skillOfferData ? getSkillOfferCommandHandle(skillOfferData) : undefined;
  const matchingCustomSkill = findMatchingCustomSkillForOffer(commandHandle, customSkillsData?.customSkills);
  const shareStatus = useCustomSkillShareStatus(matchingCustomSkill);
  const offerAction = getSkillOfferAction(matchingCustomSkill, shareStatus.hasShares);
  const isApplied = isSkillOfferFileApplied(skillOfferData?.fileUuid, customSkillsData?.customSkills);

  useEffect(() => {
    if (shareStatus.error) {
      notify.error('Unable to check whether this skill is shared.');
    }
  }, [notify, shareStatus.error]);

  if (!skillOfferData) {
    return null;
  }

  const data = skillOfferData;

  const handleDownload = () => {
    if (data.fileUuid) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_DOWNLOADED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        offerAction,
      });
      download(data.fileUuid);
    }
  };

  const handleSave = async () => {
    if (!data.fileUuid || isSaving || isApplied) {
      return;
    }

    setIsSaving(true);
    trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_STARTED, {
      chatUuid: chatOverlayContext?.chatUuid,
      commandHandle,
      fileExtension: getFileExtension(data.filename),
      fileUuid: data.fileUuid,
      offerAction,
    });
    try {
      if (matchingCustomSkill?.uuid) {
        const variables = {
          uuid: matchingCustomSkill.draftOfCustomSkillUuid ?? matchingCustomSkill.uuid,
          command: { fileUuid: data.fileUuid },
        };
        const updatedSkill = shareStatus.hasShares
          ? (await updateCustomSkillDraft({ variables })).data?.updateCustomSkillDraft
          : (await updateCustomSkill({ variables })).data?.updateCustomSkill;
        await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills()]);
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_SUCCEEDED, {
          chatUuid: chatOverlayContext?.chatUuid,
          commandHandle: updatedSkill?.command,
          fileExtension: getFileExtension(data.filename),
          fileUuid: data.fileUuid,
          offerAction,
          skillId: updatedSkill?.uuid,
          skillSource: updatedSkill?.source,
        });
        notify.success(shareStatus.hasShares ? 'Custom skill draft saved.' : 'Custom skill updated.');
        return;
      }

      const { data: createData } = await createCustomSkill({
        variables: {
          command: {
            fileUuid: data.fileUuid,
            enabled: true,
          },
        },
      });
      await Promise.all([skillsContext?.refetchSkills(), refetchCustomSkills()]);
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_SUCCEEDED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle: createData?.createCustomSkill?.command,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        offerAction,
        skillId: createData?.createCustomSkill?.uuid,
        skillSource: createData?.createCustomSkill?.source,
      });
      notify.success('Custom skill saved.');
    } catch (error) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.GENERATED_SKILL_SAVE_FAILED, {
        chatUuid: chatOverlayContext?.chatUuid,
        commandHandle,
        errorName: error instanceof Error ? error.name : undefined,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        offerAction,
      });
      notify.error(error instanceof Error ? error.message : `Unable to ${offerAction} custom skill.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SkillOfferCard
      commandHandle={commandHandle}
      filename={data.filename}
      onDownload={data.fileUuid ? handleDownload : undefined}
      onSave={
        isNovaSkillsManagementEnabled && (!matchingCustomSkill || shareStatus.isResolved)
          ? () => void handleSave()
          : undefined
      }
      action={offerAction}
      isDownloading={isDownloading}
      isSaving={isSaving}
      isSaved={isApplied}
      showSave={isNovaSkillsManagementEnabled}
    />
  );
}

function FolderArtifactDataPart({ part }: DataPartRendererProps = {}) {
  const notify = useNotify();
  const analytics = useAnalytics();
  const chatOverlayContext = useChatOverlayContextSafe();
  const isLocalFolderAttachmentsEnabled = useFeatureFlag('nova-local-folder-attachments', false);
  const [latestData] = useDataPart<FolderArtifactData>('folderArtifact');
  const data = getFolderArtifactDataForRenderer(part, latestData);
  const { download, isDownloading } = useDownloadFile({});
  // Separate hook instance: resolving the archive URL during apply preparation shares the
  // mutation's isLoading, so reusing the instance above would spin the download button.
  const { previewUrl: resolveArchiveUrl } = useDownloadFile({});
  const folderApply = useFolderArtifactApply({
    fileUuid: data?.fileUuid,
    resolveArchiveUrl,
  });

  if (data?.kind !== 'folder') {
    return null;
  }

  const handleDownload = () => {
    if (data.fileUuid) {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        chatUuid: chatOverlayContext?.chatUuid,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        source: 'folder_artifact',
      });
      download(data.fileUuid);
    }
  };

  const handleApply = async () => {
    try {
      await folderApply.startApply();
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Unable to prepare this folder.');
    }
  };

  const handleConfirmApply = async () => {
    try {
      const result = await folderApply.confirmApply();
      notify.success(
        `Applied ${result.writtenCount} file${result.writtenCount === 1 ? '' : 's'} to the selected folder.`,
      );
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_DOWNLOADED, {
        chatUuid: chatOverlayContext?.chatUuid,
        createdFiles: result.createCount,
        fileExtension: getFileExtension(data.filename),
        fileUuid: data.fileUuid,
        overwrittenFiles: result.overwriteCount,
        source: 'folder_artifact_apply',
      });
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Unable to apply this folder.');
    }
  };

  return (
    <FolderArtifactCard
      applyPlan={folderApply.plan}
      filename={data.filename}
      isApplying={folderApply.isApplying}
      isApplySupported={isLocalFolderAttachmentsEnabled && folderApply.isSupported}
      onDownload={data.fileUuid ? handleDownload : undefined}
      onApply={data.fileUuid ? () => void handleApply() : undefined}
      onCancelApply={folderApply.cancelApply}
      onConfirmApply={() => void handleConfirmApply()}
      isDownloading={isDownloading}
      isPreparingApply={folderApply.isPreparingApply}
    />
  );
}

export function ProgressDataPart() {
  const showProgressDataPart = useShowProgressDataPart();
  const [data] = useDataPart<{ message?: string }>('progress');

  if (!showProgressDataPart) {
    return null;
  }

  return <ProgressMessage message={data?.message ?? ''} />;
}

/**
 * Creates default data part renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 * Each renderer is a React component that uses useDataPart hook internally.
 */
export function createDefaultDataPartRenderers(): Record<string, DataPartRenderer> {
  return {
    coverageGapAnalysis: CoverageGapAnalysisDataPart,
    policyCheck: PolicyCheckDataPart,
    skillOffer: SkillOfferDataPart,
    folderArtifact: FolderArtifactDataPart,
  };
}

/**
 * Creates default tool output renderers.
 * Apps can extend or override these by passing their own renderers to Messages component.
 */
export function createDefaultToolRenderers(): Record<string, ToolOutputRenderer> {
  return createDefaultToolRenderersFromComponents();
}
